import { LiveQuizClientMessage, LiveQuizServerEvent } from '../../types/index.ts';

import { STORAGE_KEYS } from '../common/constants';
import { liveQuizSocketUrl } from '../common/env';

const reconnectBaseDelayMs = 500;
const reconnectMaxDelayMs = 10000;
const heartbeatIntervalMs = 25000;

export type LiveQuizConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'closed';

export interface LiveQuizSocketHandlers {
    onEvent: (event: LiveQuizServerEvent) => void;
    onStateChange: (state: LiveQuizConnectionState) => void;
}

export interface LiveQuizSocketOptions {
    sessionId: string;
    participantId?: string;
    useAuthToken: boolean;
}

export interface LiveQuizSocketController {
    send: (message: LiveQuizClientMessage) => void;
    close: () => void;
}

export function connectLiveQuizSocket(
    options: LiveQuizSocketOptions,
    handlers: LiveQuizSocketHandlers,
): LiveQuizSocketController {
    let socket: WebSocket | null = null;
    let reconnectAttempt = 0;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const clearTimers = () => {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    };

    const send = (message: LiveQuizClientMessage) => {
        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(message));
        }
    };

    const identify = () => {
        send({
            type: 'identify',
            sessionId: options.sessionId,
            participantId: options.participantId,
            token: options.useAuthToken ? (sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ?? undefined) : undefined,
        });
    };

    const scheduleReconnect = () => {
        if (disposed) {
            return;
        }
        const delay = Math.min(reconnectBaseDelayMs * 2 ** reconnectAttempt, reconnectMaxDelayMs);
        reconnectAttempt += 1;
        handlers.onStateChange('reconnecting');
        reconnectTimer = setTimeout(open, delay);
    };

    const open = () => {
        if (disposed) {
            return;
        }

        handlers.onStateChange(reconnectAttempt === 0 ? 'connecting' : 'reconnecting');
        socket = new WebSocket(liveQuizSocketUrl);

        socket.onopen = () => {
            reconnectAttempt = 0;
            handlers.onStateChange('connected');
            identify();
            heartbeatTimer = setInterval(() => send({ type: 'ping' }), heartbeatIntervalMs);
        };

        socket.onmessage = (message) => {
            let event: LiveQuizServerEvent;
            try {
                event = JSON.parse(message.data) as LiveQuizServerEvent;
            } catch {
                return;
            }
            if (event.type === 'pong') {
                return;
            }
            handlers.onEvent(event);
        };

        socket.onclose = () => {
            clearTimers();
            socket = null;
            if (disposed) {
                handlers.onStateChange('closed');
                return;
            }
            scheduleReconnect();
        };

        socket.onerror = () => socket?.close();
    };

    open();

    return {
        send,
        close: () => {
            disposed = true;
            clearTimers();
            socket?.close();
            socket = null;
            handlers.onStateChange('closed');
        },
    };
}
